// Screen "Preview now" body. A fullscreen (single-zone) screen mounts one
// player-core engine, exactly as before. A screen with a LAYOUT has a
// manifest-preview that carries `zones` (docs/09 §C1); we compose the preview from
// ONE engine per zone, each absolutely positioned by rect_pct inside the 16:9
// stage — the way the player renders zones — so the operator sees the real split.
// The overlay (if any) rides on the main zone.
//
// CONTRACT: a zone's `timeline` is manifest WINDOWS — the single ruled shape
// across the shared schema, the API, and player-core. We do NOT tolerate any
// other shape: a non-window timeline is a contract regression, so we warn and
// render that zone empty rather than silently masking the drift.

import { EnginePreview } from '../../components/EnginePreview.tsx';
import type { Manifest, ManifestWindow } from '@seencast/player-core';

/** Extract zones from a manifest-preview, or null when it's a single-zone screen. */
export function manifestZones(manifest: Manifest | null): Manifest['zones'] | null {
  const zones = manifest?.zones;
  return zones && zones.length > 0 ? zones : null;
}

/** A zone timeline is manifest WINDOWS (canonical). Anything else is a contract
 *  regression: warn and treat the zone as empty rather than hide the drift. */
export function zoneWindows(zoneId: string, timeline: unknown): ManifestWindow[] {
  if (!Array.isArray(timeline) || timeline.length === 0) return [];
  const first = timeline[0] as { items?: unknown };
  if (!first || !Array.isArray(first.items)) {
    console.warn(
      `[ScreenPreviewStage] zone "${zoneId}" timeline is not window-shaped (expected ManifestWindow[]); rendering it empty.`,
    );
    return [];
  }
  return timeline as ManifestWindow[];
}

/** Build a standalone manifest for one zone, carrying the parent screen meta. The
 *  overlay is attached only to the main zone. */
function zoneManifest(
  parent: Manifest,
  zone: NonNullable<Manifest['zones']>[number],
  withOverlay: boolean,
): Manifest {
  return {
    version: `${parent.version}-${zone.id}`,
    min_player_version: parent.min_player_version,
    screen: parent.screen,
    poll_interval_s: parent.poll_interval_s,
    valid_until: parent.valid_until,
    timeline: zoneWindows(zone.id, zone.timeline),
    fallback: { items: [] },
    ...(withOverlay && parent.overlay ? { overlay: parent.overlay } : {}),
  };
}

export function ScreenPreviewStage({
  manifest,
  screenName,
}: {
  manifest: Manifest | null;
  screenName: string;
}) {
  const zones = manifestZones(manifest);

  if (!manifest || !zones) {
    return (
      <EnginePreview
        manifest={manifest}
        emptyLabel="Nothing scheduled right now"
        ariaLabel={`Live preview of ${screenName}`}
      />
    );
  }

  return (
    <div className="engine-frame zone-stage" role="group" aria-label={`Zoned preview of ${screenName}`}>
      {zones.map((z, i) => (
        <div
          key={z.id}
          className={`zone-cell zone-cell-${z.id}`}
          style={{
            left: `${z.rect_pct.x}%`,
            top: `${z.rect_pct.y}%`,
            width: `${z.rect_pct.w}%`,
            height: `${z.rect_pct.h}%`,
          }}
        >
          <EnginePreview
            fill
            manifest={zoneManifest(manifest, z, i === 0)}
            emptyLabel=""
            ariaLabel={`${z.id} zone of ${screenName}`}
          />
        </div>
      ))}
    </div>
  );
}
